import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, X } from 'lucide-react';

const FEATURES = [
  { icon: '◈', text: '10 adaptive technical questions — MCQ, coding, grid & file upload' },
  { icon: '◇', text: 'Monaco Editor with real syntax highlighting for coding challenges' },
  { icon: '△', text: 'Live countdown timer with automated AI evaluation on submission' },
  { icon: '□', text: 'Executive analytics dashboard with Donut, Bar & Radar charts' },
  { icon: '⬡', text: 'Proctored-environment UI with anti-exit warnings & fullscreen mode' },
];

export default function ExperienceDemoModal({ open, onClose }) {
  if (!open) return null;

  const handleBeginAssessment = () => {
    window.open('/demo', '_blank', 'noopener,noreferrer');
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Overlay */}
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-50"
            style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(10px)' }}
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            key="modal"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
          >
            <div
              className="w-full max-w-md rounded-2xl overflow-hidden pointer-events-auto"
              data-testid="experience-demo-modal"
              style={{
                background: 'rgba(12,13,20,0.96)',
                border: '1px solid rgba(124,58,237,0.2)',
                boxShadow: '0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04) inset',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Gradient accent top bar */}
              <div
                className="h-0.5 w-full"
                style={{ background: 'linear-gradient(90deg, #7c3aed, #6366f1, #06b6d4)' }}
              />

              <div className="p-7">
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold"
                        style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)' }}
                      >
                        LQ
                      </div>
                      <span
                        className="text-xs font-mono uppercase tracking-widest"
                        style={{ color: 'rgba(255,255,255,0.35)', letterSpacing: '0.18em' }}
                      >
                        Assessment Platform
                      </span>
                    </div>
                    <h2 className="text-white font-semibold text-lg leading-snug">
                      Technical Assessment Demo
                    </h2>
                    <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
                      Enterprise-grade simulation · Opens in new tab
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
                    style={{ color: 'rgba(255,255,255,0.3)' }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.7)')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.3)')}
                    data-testid="demo-modal-close"
                  >
                    <X size={15} />
                  </button>
                </div>

                {/* Divider */}
                <div className="mb-5 h-px" style={{ background: 'rgba(255,255,255,0.06)' }} />

                {/* Features list */}
                <ul className="space-y-3 mb-6">
                  {FEATURES.map((f, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.15 + i * 0.06, duration: 0.3 }}
                      className="flex items-start gap-3 text-sm"
                      style={{ color: 'rgba(255,255,255,0.6)' }}
                    >
                      <span className="flex-shrink-0 mt-0.5 text-xs" style={{ color: '#7c3aed' }}>
                        {f.icon}
                      </span>
                      {f.text}
                    </motion.li>
                  ))}
                </ul>

                {/* Divider */}
                <div className="mb-5 h-px" style={{ background: 'rgba(255,255,255,0.06)' }} />

                {/* Estimated time */}
                <p className="text-xs mb-5" style={{ color: 'rgba(255,255,255,0.3)' }}>
                  Estimated duration: 35–45 minutes · No account required
                </p>

                {/* CTA */}
                <button
                  data-testid="begin-assessment-btn"
                  onClick={handleBeginAssessment}
                  className="w-full rounded-xl py-3.5 text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
                  style={{
                    background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                    color: '#fff',
                    boxShadow: '0 4px 20px rgba(124,58,237,0.4)',
                  }}
                >
                  Begin Assessment
                  <ArrowRight size={15} />
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
